import { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import React, { createContext, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SessionRenewThreshold } from '../../configs/PortalConfig';
import UseLogger from '../../hooks/useLogger/useLogger';
import useSnackBar from '../../hooks/useSnackBar/useSnackBar';
import { LogoutResponse, UserData } from '../../models/AuthModels';
import AuthService from '../../services/Auth/AuthService';
import { setNotificationsCounter } from '../../stores/NotificationsCenterStore';

interface AuthContextData {
  authenticate: (renewSession?: boolean, route?: string) => Promise<string | unknown>;
  renewCIAMSession: () => Promise<unknown>;
  logout: () => Promise<AxiosResponse<LogoutResponse> | unknown>;
  userData: UserData | null;
  impersonate: (id: string) => Promise<unknown>;
  fetchAdminInfo: () => Promise<unknown>;
}

export const AuthContext = createContext({} as AuthContextData);

function AuthProvider({ children }: PropsWithChildren) {
  const snackbar = useSnackBar();
  const dispatch = useDispatch();
  const routesWithoutAuth = ['/404', '/notAllowed', '/auth', '/logout', '/privacypolicy', '/termsandconditions'];
  const [expires, setExpires] = useState<string | null>(localStorage.getItem('expires'));
  const [userData, setUserData] = useState<UserData | null>(null);

  /**
   * Check if token is expired
   * @param timestamp
   */
  const isExpired = (timestamp: string): boolean => {
    if (!timestamp) return true;

    const expirationTimestamp = parseInt(timestamp, 10);
    return Date.now() >= expirationTimestamp;
  };

  /**
   * Requests the user session
   */
  const authenticate = async (renewSession = false, route = '') => {
    UseLogger().logInfo(`AuthContext: Performing /authenticate method to load the route: ${route}`);
    if (!expires || isExpired(expires) || renewSession) {
      if (userData) {
        UseLogger().logInfo(`AuthContext: User data already exists!`);
        const expiryTime = DateTime.fromMillis(userData.exp);
        const diff = expiryTime.diff(DateTime.now(), ['minutes']).toObject();
        if (diff.minutes && diff.minutes > SessionRenewThreshold) {
          UseLogger().logWarn(
            `AuthContext: No need to renew the session. Session is still valid for ${diff.minutes} minutes. `,
          );
          return expires;
        }
      }
      UseLogger().logInfo(`AuthContext: Performing authenticate request...`);
      return new Promise((resolve, reject) => {
        AuthService.api.authenticate().then(
          (response) => {
            if (response.data) {
              localStorage.setItem('expires', response.data.expires.toString());
              const isImpersonatingAdmin = sessionStorage.getItem('impersonateId') !== null;
              setExpires(response.data.expires.toString());
              const userDataObj: UserData = {
                exp: response.data.expires,
                email: isImpersonatingAdmin && userData?.email ? userData.email : response.data.email,
                role: isImpersonatingAdmin && userData?.role ? userData.role : response.data.role,
                firstName: isImpersonatingAdmin && userData?.firstName ? userData.firstName : response.data.firstName,
                lastName: isImpersonatingAdmin && userData?.lastName ? userData.lastName : response.data.lastName,
                canResetPassword:
                  isImpersonatingAdmin && userData?.canResetPassword
                    ? userData.canResetPassword
                    : response.data.canResetPassword,
                companyName: isImpersonatingAdmin && userData?.companyName ? userData.companyName : '',
              };
              setUserData(userDataObj);
              dispatch(setNotificationsCounter(response.data.unreadNotifications));
              UseLogger().logInfo(`AuthContext: Authentication done`);
            }
            resolve(response);
          },
          (err) => {
            snackbar.showSnackBar('Authentication failed', 'error');
            reject(err);
          },
        );
      });
    }
    UseLogger().logInfo(`AuthContext: Authentication not needed! Token still valid.`);
    return expires;
  };

  /**
   * SAML initializer
   */
  const initSaml = () => {
    UseLogger().logInfo('AuthContext: Verifying the need for login!');
    if (!routesWithoutAuth.includes(window.location.pathname) && (!expires || isExpired(expires))) {
      localStorage.removeItem('expires');
      UseLogger().logInfo('AuthContext: Performing the login on SAML...');
      window.location.href = `${import.meta.env.VITE_SAML_URL}`;
    } else {
      authenticate().then(() => {});
      UseLogger().logInfo('AuthContext: No need to perform the login!');
    }
  };

  /**
   * Request the renewal of the CIAM session
   */
  const renewCIAMSession = () =>
    new Promise((resolve, reject) => {
      AuthService.api.renewCIAMSession().then(
        (res) => {
          UseLogger().logInfo(`AuthContext: CIAM session renewed successfully!`);
          resolve(res);
        },
        (err) => {
          UseLogger().logInfo(`AuthContext: Failed to renew the CIAM session!`);
          snackbar.showSnackBar('Failed to renew your session!', 'error');
          reject(err);
        },
      );
    });

  /**
   * Performs a logout action
   */
  const logout = async () =>
    new Promise((resolve, reject) => {
      AuthService.api.logout().then(
        (res) => {
          UseLogger().logInfo(`AuthContext: Logout successful: ${JSON.stringify(res)}`);
          localStorage.removeItem('expires');
          resolve(res);
        },
        (err) => {
          UseLogger().logInfo(`AuthContext: Logout error: ${JSON.stringify(err)}`);
          reject(err);
        },
      );
    });

  /**
   * Retrives admin info from id
   */
  const fetchAdminInfo = () =>
    new Promise((resolve, reject) => {
      AuthService.api.fetchAdminInfo().then(
        (response) => {
          const userDataObj: UserData = {
            exp: response.data.expires,
            email: response.data.email,
            role: response.data.role,
            firstName: response.data.firstName,
            lastName: response.data.lastName,
            canResetPassword: response.data.canResetPassword,
            companyName: response.data.company.name,
          };
          setUserData(userDataObj);
          dispatch(setNotificationsCounter(response.data.unreadNotifications));
          resolve(response);
        },
        (err) => {
          UseLogger().logInfo(`AuthContext: Failed to fetch admin info. Error: ${JSON.stringify(err)}`);
          reject(err);
        },
      );
    });

  /**
   * Impersonates an admin
   */
  const impersonate = async (id: string) => {
    UseLogger().logInfo(`AuthContext: Performing impersonate request...`);
    return new Promise((resolve, reject) => {
      AuthService.api.impersonate(id).then(
        (response) => {
          if (response.data) {
            sessionStorage.setItem('impersonateId', id);
            const userDataObj: UserData = {
              exp: userData ? userData.exp : response.data.expires,
              email: response.data.email,
              role: response.data.role,
              firstName: response.data.firstName,
              lastName: response.data.lastName,
              canResetPassword: response.data.canResetPassword,
              companyName: response.data.company.name,
            };
            setUserData(userDataObj);
            dispatch(setNotificationsCounter(response.data.unreadNotifications));
            UseLogger().logInfo(`AuthContext: Impersonation done`);
          }
          resolve(response);
        },
        (err) => {
          snackbar.showSnackBar('Impersonation failed', 'error');
          reject(err);
        },
      );
    });
  };

  /**
   * User data initializer
   */
  const initUserData = () => {
    if (localStorage.getItem('expires')) {
      fetchAdminInfo();
    }
  };

  useEffect(() => {
    UseLogger().log(`AuthContext: init auth context...`);
    initUserData();
    initSaml();
  }, []);

  const value = useMemo(
    () => ({
      authenticate,
      renewCIAMSession,
      logout,
      userData,
      impersonate,
      fetchAdminInfo,
    }),
    [userData],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export default AuthProvider;
